import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Mic, Square, Loader2, Check, X } from "lucide-react";

// Record → transcribe (whisper-1) → edit → log as note/call on the lead timeline. Audio is not stored.
export default function VoiceMemo({ leadId, onLogged }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | review
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const r = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        setState("transcribing");
        const blob = new Blob(chunks.current, { type: mime });
        const fd = new FormData();
        fd.append("audio", blob, mime.includes("webm") ? "memo.webm" : "memo.mp4");
        fd.append("kind", kind);
        try {
          const { data } = await api.post(`/leads/${leadId}/voice-memo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setText(data.transcript); setState("review");
        } catch (e) { toast.error(e.response?.data?.detail || "Transcription failed"); setState("idle"); }
      };
      r.start(); rec.current = r; setSecs(0); setState("recording");
      timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch { toast.error("Microphone access was denied"); }
  };
  const stop = () => rec.current?.state === "recording" && rec.current.stop();
  const save = async () => {
    try {
      if (kind === "call") await api.post(`/leads/${leadId}/activities`, { type: "call", channel: "voice", summary: "Call note (voice memo)", detail: text }).catch(async () => api.post(`/leads/${leadId}/note`, { text: `Call note (voice memo): ${text}` }));
      else await api.post(`/leads/${leadId}/note`, { text });
      toast.success("Memo logged to timeline"); setState("idle"); setText(""); onLogged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log memo"); }
  };

  if (state === "review") return (
    <div className="w-full border border-[#3E8F99]/40 bg-[#3E8F99]/5 p-3 space-y-2" data-testid="voice-memo-review">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#2c6b73]">Transcript · edit before logging
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="voice-memo-kind" className="ml-auto border border-[#DFDAD0] bg-white px-2 py-0.5 text-xs normal-case tracking-normal"><option value="note">Note</option><option value="call">Call note</option></select></div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="voice-memo-text" className="w-full border border-[#DFDAD0] bg-white px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
      <div className="flex gap-2">
        <button onClick={save} disabled={!text.trim()} data-testid="voice-memo-save" className="flex items-center gap-1.5 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-sm disabled:opacity-40"><Check className="w-3.5 h-3.5" /> Log to timeline</button>
        <button onClick={() => { setState("idle"); setText(""); }} data-testid="voice-memo-discard" className="flex items-center gap-1.5 border border-[#DFDAD0] px-3 py-1.5 text-sm"><X className="w-3.5 h-3.5" /> Discard</button>
      </div>
    </div>
  );
  return (
    <button onClick={state === "recording" ? stop : state === "idle" ? start : undefined} disabled={state === "transcribing"} data-testid="voice-memo-btn" title="Dictate a note"
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase border ${state === "recording" ? "bg-[#B23A2E] text-white border-[#B23A2E] animate-pulse" : "border-[#DFDAD0] hover:bg-[#EFECE5]"} disabled:opacity-60`}>
      {state === "recording" ? <><Square className="w-3.5 h-3.5" /> Stop · {secs}s</> : state === "transcribing" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing…</> : <><Mic className="w-3.5 h-3.5" /> Dictate</>}
    </button>
  );
}
